import type { AcceptOutcome } from './accept-rules';
import type { AmbientDecisionRow } from './decision-types';

/**
 * PROD-2650. What an accept put in the chart, so an Undo can take it out again.
 *
 * Pure. No network, no React.
 *
 * WHY THIS EXISTS. Undo on an accepted diagnosis used to append an `Undone` decision and leave the
 * Assessment & Plan row that the accept created. The decision record then said "reversed" while
 * the row stayed in the note, went into the signed note and reached the HIEs. The panel had
 * nothing to delete because it never learned which row the accept wrote. Now the host reports the
 * row id with the outcome, the panel records it on the decision (`writtenEntityType` /
 * `writtenEntityId`, which Scrips.Patient already stores), and Undo targets that row.
 */

/** `WrittenEntityType` for a diagnosis the accept added to the Assessment & Plan canvas. */
export const AP_DIAGNOSIS_ENTITY = 'AssessmentPlanDiagnosis';

/** `WrittenEntityType` for a reported medication charted through `CreateMedication isOrder:false`. */
export const PATIENT_MEDICATION_ENTITY = 'PatientMedication';

/** The decision kind for a reported / continuing medication charted from an ambient mention. */
export const REPORTED_MEDICATION_KIND = 'reported-medication';

export interface WrittenEntity {
  entityType: string;
  entityId: string;
}

/**
 * The host's answer when it can say WHICH row it wrote. Only `'written'` carries `written`, and only
 * when the click itself created the row. A duplicate (the diagnosis was already on the canvas) is
 * `'written'` with no `written`: the finding is in the chart, but this accept did not put it there,
 * so Undo has no row of its own to remove.
 */
export interface AcceptReceipt {
  outcome: AcceptOutcome;
  written?: WrittenEntity;
  /**
   * When the write is LOCAL first and reaches the server later (the A&P canvas autosaves), whether
   * it got there: resolves true once a save carrying it succeeded, false if it was undone locally
   * before any did. The panel records the decision as Written only on true. Absent means the write
   * is already durable when the host answers.
   */
  persisted?: Promise<boolean>;
}

/** What `onAcceptProposal` may return. The bare outcome stays valid for hosts that write nothing. */
export type AcceptResult = AcceptOutcome | AcceptReceipt;

export function outcomeOf(result: AcceptResult | void): AcceptOutcome | undefined {
  if (!result) return undefined;
  return typeof result === 'string' ? result : result.outcome;
}

/** The row the accept wrote, if it wrote one and said so. Never set on a non-written outcome. */
export function writtenOf(result: AcceptResult | void): WrittenEntity | undefined {
  if (!result || typeof result === 'string' || result.outcome !== 'written') return undefined;
  return result.written;
}

/** The `persisted` promise of a written receipt, if the host gave one. */
export function persistedOf(result: AcceptResult | void): Promise<boolean> | undefined {
  if (!result || typeof result === 'string' || result.outcome !== 'written') return undefined;
  return result.persisted;
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `WrittenEntityId` is a `Guid?` on the server; anything else would fail model binding. */
export const isGuid = (value: string | null | undefined): value is string => !!value && GUID.test(value);

/** What the host needs to find the row an accept wrote. */
export interface RetractTarget {
  entityType: string;
  entityId: string;
  proposalKind: string;
  /** The server's normalised key: for a diagnosis, the lower-cased ICD code. */
  proposalKey: string;
  label: string;
}

/**
 * The host's answer to "can you take this row out?", given BEFORE the undo is recorded.
 *
 *   ok: false  the row cannot be removed right now. The undo is not recorded and `message` is shown.
 *   ok: true   the row can be removed (or is already gone). `commit` runs only once the server has
 *              stored the undo, so a refused undo (409 on a signed note) removes nothing.
 */
export type RetractPlan =
  /**
   * `commit` runs after the undo is stored. It returns a sentence when it had to leave the row after
   * all (the A&P changed while the undo was in flight): the panel shows it, so the record saying
   * "undone" and a row still in the note never disagree silently.
   */
  | { ok: true; commit: () => string | void }
  | { ok: false; message: string };

/**
 * How Undo treats a decision row.
 *
 *   'retract'      a written accept that names its A&P row: remove the row, then record the undo.
 *   'notice'       a written accept of a diagnosis with no row of its own (a duplicate, or a row
 *                  recorded before PROD-2650): record the undo and say plainly that the diagnosis
 *                  is still in the A&P.
 *   'not-undoable' a reported medication: Undo is not offered, the record says how to reverse it.
 *   'record'       everything else (a decline, a chief complaint, an accept that wrote nothing).
 */
export type UndoMode = 'retract' | 'notice' | 'not-undoable' | 'record';

export function undoModeOf(row: AmbientDecisionRow): UndoMode {
  if (row.decision !== 'Accepted') return 'record';
  if (row.writtenEntityType === PATIENT_MEDICATION_ENTITY || row.proposalKind === REPORTED_MEDICATION_KIND) {
    return 'not-undoable';
  }
  // PROD-3183: accepted dictated vitals are a vitals capture. They are changed on the vitals card.
  if (row.proposalKind === VITALS_DECISION_KIND) return 'not-undoable';
  // PROD-3183: an accepted ROS / exam / risk finding is a tick or a notes line in that section. It is
  // changed in the section, like any other answer there.
  if (isFindingDecisionKind(row.proposalKind)) return 'not-undoable';
  if (row.acceptOutcome !== 'Written') return 'record';
  if (row.writtenEntityType === AP_DIAGNOSIS_ENTITY && row.writtenEntityId) return 'retract';
  if (row.proposalKind === 'diagnosis') return 'notice';
  return 'record';
}

/**
 * WHO MAY UNDO A DECISION: ITS AUTHOR (authority file: "each entry is owned by its author and
 * edited only by its author").
 *
 * The author is the row's `decidedBy`, the JWT `sub` the server stamped. The viewer is the same
 * claim (`AmbientDecisionPort.currentUserId`). Undo is offered only when the two match. A row with
 * NO recorded author (blank, or the empty GUID the server writes when it could not read a `sub`)
 * cannot be attributed to anyone, so it falls to the signed-in physician, and the record says so.
 *
 * Offering it to every viewer let a nurse, or another clinician, Undo a physician's "Add to A&P"
 * accept, and that removes the physician's diagnosis row from the note.
 */
export interface UndoAuthority {
  mayUndo: boolean;
  /** Shown beside the row when it explains something: why Undo is absent, or why it is present. */
  note?: string;
}

const EMPTY_GUID = /^0{8}-0{4}-0{4}-0{4}-0{12}$/;

export function undoAuthority(
  row: Pick<AmbientDecisionRow, 'decidedBy'>,
  currentUserId: string | undefined,
  viewerIsPhysician: boolean,
): UndoAuthority {
  const author = row.decidedBy?.trim() ?? '';
  if (!author || EMPTY_GUID.test(author)) {
    return viewerIsPhysician
      ? { mayUndo: true, note: 'No author was recorded for this decision, so the signed-in physician may undo it.' }
      : { mayUndo: false, note: 'No author was recorded for this decision, so only a physician can undo it.' };
  }
  const viewer = currentUserId?.trim().toLowerCase();
  return viewer && viewer === author.toLowerCase()
    ? { mayUndo: true }
    : { mayUndo: false, note: 'Only the clinician who made this decision can undo it.' };
}

/** Said after an undo that could not remove anything, so the chart and the record never disagree silently. */
export const stillInApNotice = (label: string) =>
  `Undo recorded. ${label} is still in the Assessment & Plan, because this accept did not add a row of ` +
  'its own (for example, it was already there). Remove it there if it should not be in the note.';

/** PROD-3183: the decision kind of the grouped "Dictated vitals" suggestion (`dictated-vitals-proposal.ts`). */
export const VITALS_DECISION_KIND = 'vitals';

/** Shown in place of Undo on accepted dictated vitals. */
export const VITALS_UNDO_HINT = 'To change these, edit the vitals. The record keeps the accept.';

/**
 * PROD-3183: the decision kinds of the ROS, physical-exam and risk finding suggestions
 * (`finding-placement.ts`). Each needs an active row in the server's action register.
 */
export const FINDING_DECISION_KINDS = { ros: 'ros', exam: 'exam', risk: 'risk' } as const;

export const isFindingDecisionKind = (kind: string | undefined): boolean =>
  kind === FINDING_DECISION_KINDS.ros || kind === FINDING_DECISION_KINDS.exam || kind === FINDING_DECISION_KINDS.risk;

/** Shown in place of Undo on an accepted finding. */
export const FINDING_UNDO_HINT = 'To change this, edit the section. The record keeps the accept.';

/** The sentence shown in place of Undo on a row `undoModeOf` calls not undoable. */
export function notUndoableHint(row: AmbientDecisionRow): string {
  if (row.proposalKind === VITALS_DECISION_KIND) return VITALS_UNDO_HINT;
  if (isFindingDecisionKind(row.proposalKind)) return FINDING_UNDO_HINT;
  return REPORTED_MEDICATION_UNDO_HINT;
}

/** Shown in place of Undo on a reported medication. */
export const REPORTED_MEDICATION_UNDO_HINT =
  'To reverse this, remove it from the medication list. The record keeps the accept.';

/**
 * The canvas row an accept wrote: by id first (same session), then by code (after a reload the
 * canvas carries the server's child ids, so the client id the accept recorded is gone). A diagnosis
 * accept only writes on an exact code match against Master, so the row's code IS the proposal key.
 */
export function findWrittenDiagnosis<T extends { id: string; code?: string }>(
  rows: readonly T[],
  target: Pick<RetractTarget, 'entityId' | 'proposalKey'>,
): T | undefined {
  const byId = rows.find((row) => row.id === target.entityId);
  if (byId) return byId;
  const key = target.proposalKey.trim().toLowerCase();
  if (!key) return undefined;
  return rows.find((row) => row.code?.trim().toLowerCase() === key);
}
